<template>
  <section v-for="(items, category) in categories" :key="category">
    <div class="mb-2 px-6">
      <h4 class="text-base font-medium capitalize">
        {{ category.toString().replaceAll('_', ' ') }}
      </h4>
    </div>
    <ul class="space-y-2 divide-y px-6">
      <ResultItem
        v-for="(item, index) in items"
        :key="getItemKey(item, index)"
        :item="item"
        :category="category"
        :highlighted="highlightedIndex === findHighlightedIndex(item)"
        @mouseover="emit('mouseover', findHighlightedIndex(item))"
        @click="emit('click')"
      />
    </ul>
  </section>
</template>

<script setup lang="ts">
import { nanoid } from 'nanoid';
import { computed, type PropType } from 'vue';

import type { Results } from '@/entities/search';

import ResultItem from '../result-item.vue';

const emit = defineEmits(['click', 'mouseover']);

const props = defineProps({
  results: {
    type: Array as PropType<Results[]>,
    required: true,
  },
  highlightedIndex: {
    type: Number,
    default: -1,
  },
});

const _results = computed(() => props.results.map((result) => ({ ...result, _uuid: nanoid() })));

const categories = computed(() => {
  const list: { [key: string]: (Results & { _uuid: string })[] } = {};

  _results.value.forEach((item) => {
    if (list[item.type] === undefined) {
      list[item.type] = [];
    }
  });

  Object.keys(list).forEach((category) => {
    _results.value.forEach((item) => {
      if (item.type === category) {
        list[item.type]?.push(item);
      }
    });
  });

  return list;
});

const findHighlightedIndex = (item: Results & { _uuid?: string }) => {
  return _results.value.findIndex((_result) => _result._uuid === item._uuid);
};

const getItemKey = (item: Results, index: number) => {
  if ('id' in item) return `id-${item.id}`;
  if ('uuid' in item) return `uuid-${item.uuid}`;
  if ('name' in item) return `name-${item.name}`;
  return `index-${index}`;
};
</script>
